import React from 'react';
import classnames from 'classnames';
import uniq from 'lodash/uniq';
import { observer } from 'mobx-react';
import { RecurringSubscriptionStatus, SubscriptionOrderStatus } from 'chaika-enums';

import style from './Chat.module.scss';

import { translate } from '@chaika/i18n/helper';

import { useDataLoading } from '@utils/hooks/useDataLoading';

import { MeatballButton, MeatballModifier, MeatballThemes } from '@components/ActionButtons/MeatballButton';
import { IconType } from '@components/Icon';
import { Tooltip, TooltipPlace } from '@components/Tooltip';

import { ehrService } from '@patient/services/ehr/ehrService';
import { patientService } from '@patient/services/patient/patientService';
import { subscriptionService } from '@patient/services/subscriptionService';

import { userAssistantService } from '@modules/user/services/userAssistantService';

import { store } from '@store';
import { useUsersMap } from '@user/hooks/useUsersMap';
import { getInitials } from '@user/utils/getInitials';

import { useDoctorChatOptions } from './hooks/useDoctorChatOptions';
import { useAssistantChatOptions } from './hooks/useAssistantChatOptions';
import { useChatCreation } from './hooks/useChatCreation';
import { isDefined } from '@utils/isDefined';

import Clock from './clock.svg';
import Person from './person.svg';

interface Props {
    fromEntry?: boolean;
    hideLabel?: boolean;
}

export const Chat = observer(({ fromEntry, hideLabel }: Props): React.ReactElement<any> => {
    const { authUser, ehr } = store;

    const {
        chats,
        doPatientHasChats,
        subscriptionOrders,
        recurringSubscriptions,
        hasActiveSubscription,
        hasTrialSubscription,
        isSent,
        isUserBelongToPractice,
        onChatCreation,
    } = useChatCreation({ patientId: ehr.id, fromEntry });

    const { options: doctorOptions } = useDoctorChatOptions({
        chats,
        patientId: ehr.id,
        subscriptionOrders,
        recurringSubscriptions,
        onChatCreation: onChatCreation.bind(null, authUser.id),
    });

    const { options: assistantOptions } = useAssistantChatOptions({
        chats,
        doPatientHasChats,
        patientId: ehr.id,
        isUserBelongToPractice,
        onChatCreation: onChatCreation.bind(null, undefined),
        onPracticeChatCreation: onChatCreation.bind(null, undefined, true),
    });

    useDataLoading(async () => {
        if (!!ehr.id && !!authUser.id) {
            await patientService.loadByIds([ehr.id]);
            await Promise.all([
                ehrService.loadAssistantsChatLink(),
                subscriptionService.loadPatientSubscriptions(ehr.id, new Date().toISOString()),
                userAssistantService.loadUserPracticeDoctors(authUser.id),
            ]);
        }
    }, [authUser.id, ehr.id]);

    const doctorIds = uniq(
        [
            ...subscriptionOrders.filter(so => so.status === SubscriptionOrderStatus.Active).map(so => so.doctorId),
            ...recurringSubscriptions
                .filter(rs => rs.status === RecurringSubscriptionStatus.Active)
                .map(rs => rs.doctorId),
        ].filter(isDefined),
    );

    const subscriptionDoctorsById = useUsersMap(doctorIds);
    const subscriptionDoctorNames = doctorIds
        .map(id => {
            const doctor = subscriptionDoctorsById.get(id);
            return doctor && getInitials(doctor);
        })
        .filter(isDefined);

    const isTrial = hasTrialSubscription;
    const subscriptionText = isTrial
        ? translate('trialSubscriptionShort')
        : subscriptionDoctorNames.length > 0
          ? `${translate('subscription')} (${subscriptionDoctorNames[0]}${
                subscriptionDoctorNames.length > 1 ? ` + ${subscriptionDoctorNames.length - 1}` : ''
            })`
          : translate('subscription');

    return (
        <div className={style.root}>
            <div className={style.chatWrapper}>
                <MeatballButton
                    label={hideLabel ? undefined : 'telegram'}
                    theme={MeatballThemes.Secondary}
                    modifiers={[MeatballModifier.Telegram]}
                    customIcon={isSent ? IconType.CHECK_REDESIGN : IconType.PAPER_PLANE}
                    items={authUser.isDoctor ? doctorOptions : assistantOptions}
                />
                {!doPatientHasChats && <div className={style.noChat}>({translate('no')} Chaika chat)</div>}
            </div>
            {hasActiveSubscription || hasTrialSubscription ? (
                store.viewPort.isTableSize ? (
                    <div className={style.hasSubscriptionContainer}>
                        <Tooltip
                            title={subscriptionText}
                            place={TooltipPlace.Bottom}
                        >
                            <div className={classnames([style.hasSubscription, style.hasSubscriptionIcon])}>
                                {isTrial ? <Clock /> : <Person />}
                            </div>
                        </Tooltip>
                    </div>
                ) : (
                    <div
                        className={classnames([style.hasSubscriptionContainer, style.hasSubscription])}
                        title={subscriptionText}
                    >
                        {isTrial ? <Clock /> : <Person />}
                        {subscriptionText}
                    </div>
                )
            ) : null}
        </div>
    );
});
